import moment from "moment";
import { v4 as uuidv4 } from "uuid";
import { Controller, useForm } from "react-hook-form";
import { useSelector } from "react-redux";
import { useState, useEffect } from "react";
import { getPolicyDetails } from "service/policy/getPolicyDetails";
import { useTranslation } from "react-i18next";
import { isPolicyInactiveByPolicyStatus } from "js/utilities";
import { states } from "js/placeholderData/states";
import { useContext } from "react";
import { AgentData } from "context/Agent/DataProvider";

import SSNInput from "components/General/Form/SSNInput";
import FormInputError from "components/General/FormInputError";
import EverlakeLoadingOverlay from "components/General/EverlakeLoadingOverlay";
import AgentAutoCompleteComboBox from "components/General/AutoCompleteComboBox/AgentAutoCompleteComboBox";

export default function FACStepOne({
  selectedPolicy,
  setSelectedPolicy,
  formStep,
  setFormStep,
  setFACFormData,
  setLoggedUser,
  contractID,
}) {
  let debounceFunction; //timer identifier
  let debounceTimer = 1500; //time in ms, 1.5 seconds for example
  const { user } = useSelector((s) => s.user);

  const { agentPolicies } = useContext(AgentData);
  const { t } = useTranslation();
  const [selectedPolicyId, setSelectedPolicyId] = useState("");
  const [policyFetching, setPolicyFetching] = useState(false);
  const [isDateOfDeathValid, setIsDateOfDeathValid] = useState(true);
  const [policyFilterOptions, setPolicyFilterOptions] = useState([]);
  const [isDisabled, setIsDisabled] = useState(true);

  const uuid = uuidv4();
  const {
    register: FACStepOneRegister,
    watch,
    setValue,
    getValues,
    reset,
    control,
    setError,
    clearErrors,
    handleSubmit: FACStepOneHandleSubmit,
    formState: { errors },
  } = useForm();

  const maritalStatus = watch("marital");
  const foreignDeath = watch("foreign");
  const ownerName = watch("ownerName");

  useEffect(() => {
    let policySelectionArrayForDocTab = [];
    agentPolicies.map((p) => {
      policySelectionArrayForDocTab.push({
        value: p.contractID,
        isInActive: isPolicyInactiveByPolicyStatus(p?.status),
        displayAsValue: p.oldPolicy,
        displayAs: `${p.oldPolicy}: ${p.insuredFullName}`,
        ...p,
      });
      setPolicyFilterOptions([
        {
          value: "all",
          displayAs: t("AgentFACStepOne-select-contract"),
          status: "",
          displayAsValue: t("AgentFACStepOne-select-contract"),
        },
        ...policySelectionArrayForDocTab,
      ]);
    });
    if (contractID) {
      let policy = agentPolicies?.find((p) => p.contractID === contractID);

      if (isPolicyInactiveByPolicyStatus(policy?.status)) {
        setIsDisabled(true);
      } else {
        setIsDisabled(false);
      }
      let party = policy?.parties?.find(
        (party) => party?.person?.type?.toLowerCase() === "primary owner"
      );
      if (party) {
        setParty(party);
      }
      setValue("policyNumber", contractID);
      setSelectedPolicyId(contractID);
    }
  }, [agentPolicies, contractID]);

  const onSubmit = (data) => {
    setFormStep(2);
    setFACFormData((currentFormData) => {
      return {
        ...currentFormData,
        ...data,
      };
    });
  };

  useEffect(() => {
    async function fetchDataPolicyData() {
      setPolicyFetching(true);
      await getPolicyDetails(selectedPolicyId).then((data) => {
        setSelectedPolicy(data);
        setPolicyFetching(false);
      });
    }

    if (!selectedPolicyId || selectedPolicyId === "all") {
      setValue("ownerFirstName", "");
      setValue("ownerLastName", "");
      setValue("ssn", "");
    } else {
      const policy = agentPolicies.find(
        (p) => p.contractID === selectedPolicyId
      );
      if (!isPolicyInactiveByPolicyStatus(policy?.status))
        fetchDataPolicyData();
    }
  }, [selectedPolicyId]);

  const setParty = (party) => {
    setValue("ownerName", party.person.extendedName);
    setValue("ownerFirstName", party.person.firstName);
    setValue("ownerLastName", party.person.lastName);
    setLoggedUser(party);
  };

  useEffect(() => {
    let party = selectedPolicy?.parties?.find(
      (party) => party?.person?.type.toLowerCase() === "primary owner"
    );
    if (party) {
      setParty(party);
    }
  }, [selectedPolicy]);

  return (
    <>
      {formStep === 1 && (
        <EverlakeLoadingOverlay
          isLoading={policyFetching}
          loadingText={t("AgentFACStepOne-loading-text")}
        >
          <form onSubmit={FACStepOneHandleSubmit(onSubmit)}>
            <p
              id={`${uuid}-pn-note`}
              className="text-xs text-ui-red font-bold mb-8 italic"
            >
              {t("AgentFACStepOne-requiredtext")}
            </p>
            <hr />
            <h2 className="flex flex-grow font-body font-bold text-lg mb-2 tracking-tight">
              {t("AgentFACStepOne-policyinformation")}
            </h2>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              <div
                className={`grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr]`}
              >
                <div className="md:pb-6">
                  <Controller
                    name="policyNumber"
                    control={control}
                    render={() => (
                      <AgentAutoCompleteComboBox
                        labelName={t("AgentFACStepOne-policynumber")}
                        inputPlaceholderText={t("AgentFACStepOne-policyselect")}
                        options={policyFilterOptions}
                        selectedOption={selectedPolicyId}
                        tooltipVerbiage={t("AgentFACStepOne-tooltip-verbiage")}
                        setSelectedOption={(option) => {
                          setSelectedPolicyId(option?.value);
                          setIsDisabled(true);
                          if (option?.value !== "all" && !option.isInActive) {
                            setIsDisabled(false);
                            setValue("policyNumber", option.value);
                            clearErrors("policyNumber");
                          } else {
                            setError("policyNumber", {
                              message: t("AgentFACStepOne-required-message"),
                            });
                            setValue("policyNumber", "");
                          }
                        }}
                      />
                    )}
                    rules={{
                      required: t("AgentFACStepOne-required-message"),
                    }}
                  />
                  <FormInputError
                    id={`${uuid}-pn-error`}
                    errorMessage={errors.policyNumber?.message}
                  />
                </div>
                <div></div>

                {ownerName ? (
                  <div>
                    <label
                      htmlFor={`${uuid}-owner-name`}
                      className="block mb-1"
                    >
                      {t("AgentFACStepOne-policyownername")}
                    </label>
                    <input
                      id={`${uuid}-owner-name`}
                      type="text"
                      title={ownerName}
                      placeholder={t(
                        "AgentFACStepOne-policyownername-placeholder"
                      )}
                      readOnly
                      {...FACStepOneRegister("ownerName")}
                    />
                  </div>
                ) : (
                  <>
                    <div>
                      <label
                        htmlFor={`${uuid}-owner-fn`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepOne-policyowner-firstname")}
                      </label>
                      <input
                        id={`${uuid}-owner-fn`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepOne-ownerfirstname-placeholder"
                        )}
                        disabled
                        {...FACStepOneRegister("ownerFirstName")}
                      />
                    </div>
                    <div>
                      <label
                        htmlFor={`${uuid}-owner-ln`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepOne-ownerlastname")}
                      </label>
                      <input
                        id={`${uuid}-owner-ln`}
                        type="text"
                        placeholder={t(
                          "AgentFACStepOne-ownerlastname-placeholder"
                        )}
                        disabled
                        {...FACStepOneRegister("ownerLastName")}
                      />
                    </div>
                  </>
                )}
              </div>
            </div>
            <h2 className="flex flex-grow font-body font-bold text-lg mb-2 mt-8 tracking-tight">
              {t("AgentFACStepOne-deceasedInformation")}
            </h2>
            <div className="grid gap-4 grid-cols-1 lg:gap-6">
              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr,1fr]">
                <div>
                  <label htmlFor={`${uuid}-fn`} className="block mb-1">
                    {t("AgentFACStepOne-insuredFirstName")}
                  </label>
                  <input
                    id={`${uuid}-fn`}
                    type="text"
                    disabled={isDisabled}
                    placeholder={t(
                      "AgentFACStepOne-insuredFirstNamePlaceholder"
                    )}
                    {...FACStepOneRegister("firstName", {
                      required: t(
                        "AgentFACStepOne-insuredFirstNameRequiredMessage"
                      ),
                    })}
                  />
                  <FormInputError
                    id={`${uuid}-fn-error`}
                    errorMessage={errors.firstName?.message}
                  />
                </div>

                <div>
                  <label htmlFor={`${uuid}-ln`} className="block mb-1">
                    {t("AgentFACStepOne-insuredLastName")}
                  </label>
                  <input
                    id={`${uuid}-ln`}
                    type="text"
                    disabled={isDisabled}
                    placeholder={t(
                      "AgentFACStepOne-insuredLastNamePlaceholder"
                    )}
                    {...FACStepOneRegister("lastName", {
                      required: t(
                        "AgentFACStepOne-insuredLastNameRequiredMessage"
                      ),
                    })}
                  />
                  <FormInputError
                    id={`${uuid}-ln-error`}
                    errorMessage={errors.lastName?.message}
                  />
                </div>

                <div>
                  <label htmlFor={`${uuid}-mn`} className="block mb-1">
                    {t("AgentFACStepOne-middleInitialLabel")}
                  </label>
                  <input
                    id={`${uuid}-mn`}
                    type="text"
                    disabled={isDisabled}
                    {...FACStepOneRegister("middleName")}
                  />
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,1fr,1fr]">
                <div>
                  <label htmlFor={`${uuid}-dob`} className="block mb-1">
                    {t("AgentFACStepOne-DOBLabel")}
                  </label>
                  <input
                    id={`${uuid}-dob`}
                    disabled={isDisabled}
                    type="date"
                    {...FACStepOneRegister("dob", {
                      required: t("AgentFACStepOne-DOBrequiredMessage"),
                    })}
                    max={moment()
                      .add(-1, "days")
                      .format("YYYY-MM-DD")
                      .toString()}
                  />
                  <FormInputError
                    id={`${uuid}-dob-error`}
                    errorMessage={errors.dob?.message}
                  />
                </div>

                <div className="relative">
                  <label id={`${uuid}-ssn-label`} className="block mb-1">
                    {t("AgentFACStepOne-socialSecurityLabel")}
                  </label>
                  <SSNInput
                    name="ssn"
                    describedby={`${uuid}-ssn-label`}
                    register={FACStepOneRegister}
                    setValue={setValue}
                    length="full"
                    isDisabled={isDisabled}
                  />
                  <FormInputError
                    id={`${uuid}-ssn-error`}
                    errorMessage={errors.ssn?.message}
                  />
                </div>

                <div>
                  <label
                    htmlFor={`${uuid}-insured-tax-state`}
                    className="block mb-1"
                  >
                    {t("AgentFACStepOne-stateLabel")}
                  </label>
                  <select
                    disabled={isDisabled}
                    id={`${uuid}-insured-tax-state`}
                    {...FACStepOneRegister("insuredTaxState", {
                      // required: "Please enter insured tax state",
                    })}
                  >
                    <option value="">
                      {t("AgentFACStepOne-stateResidenceLabel")}
                    </option>
                    {states.map((state, idx) => (
                      <option
                        value={state.abbreviation}
                        key={`state - ${state.name} - ${idx}`}
                      >
                        {state.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1fr,2.05fr]">
                <div>
                  <label htmlFor={`${uuid}-dod`} className="block mb-1">
                    {t("AgentFACStepOne-dateOfDeathLabel")}
                  </label>
                  <input
                    id={`${uuid}-dod`}
                    type="date"
                    disabled={isDisabled}
                    max={moment().format("YYYY-MM-DD").toString()}
                    {...FACStepOneRegister("dod")}
                    onKeyUp={() => {
                      clearTimeout(debounceFunction);
                      debounceFunction = setTimeout(async () => {
                        if (
                          getValues("dod") &&
                          moment(getValues("dod")).format("YYYY-MM-DD") >
                            moment().format("YYYY-MM-DD")
                        )
                          setIsDateOfDeathValid(false);
                        else setIsDateOfDeathValid(true);
                      }, debounceTimer);
                    }}
                    onKeyDown={() => {
                      clearTimeout(debounceFunction);
                    }}
                  />
                  {!isDateOfDeathValid && (
                    <FormInputError
                      errorMessage={"Please select valid date of death."}
                    />
                  )}
                  <div className="inline-block mt-2 mr-6">
                    <label className="inline-flex items-center text-xs font-normal">
                      <input
                        id={`${uuid}-unknown`}
                        type="checkbox"
                        disabled={isDisabled}
                        className="mr-1"
                        {...FACStepOneRegister("dodUnknown")}
                      />
                      {t("AgentFACStepOne-unknownLabel")}
                    </label>
                  </div>
                </div>
                <div className="mt-4 ">
                  <fieldset>
                    <legend>{t("AgentFACStepOne-mannerOfDeath")}</legend>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner1`}
                          type="radio"
                          name="manner"
                          disabled={isDisabled}
                          className="mr-1"
                          value="Natural"
                          {...FACStepOneRegister("manner")}
                        />{" "}
                        {t("AgentFACStepOne-naturalLabel")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner2`}
                          type="radio"
                          name="manner"
                          disabled={isDisabled}
                          className="mr-1"
                          value="Accidental"
                          {...FACStepOneRegister("manner")}
                        />{" "}
                        {t("AgentFACStepOne-accidentalLabel")}
                      </label>
                    </div>

                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner3`}
                          type="radio"
                          name="manner"
                          disabled={isDisabled}
                          className="mr-1"
                          value="other"
                          {...FACStepOneRegister("manner")}
                        />
                        {t("AgentFACStepOne-otherLabel")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-manner4`}
                          type="radio"
                          name="manner"
                          className="mr-1"
                          disabled={isDisabled}
                          value="Unknown"
                          {...FACStepOneRegister("manner")}
                        />
                        {t("AgentFACStepOne-unknownLabel")}
                      </label>
                    </div>
                  </fieldset>
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[1.5fr,2.5fr]">
                <div>
                  <fieldset>
                    <legend>
                      {t("AgentFACStepOne-stepOneRegisterQuestion")}
                    </legend>

                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-sm font-normal">
                        <input
                          id={`${uuid}-foreign-1`}
                          type="radio"
                          disabled={isDisabled}
                          className="mr-1"
                          value="yes"
                          {...FACStepOneRegister("foreign")}
                        />
                        {t("AgentFACStepOne-stepOneRegisterOption1")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-sm font-normal">
                        <input
                          id={`${uuid}-foreign-2`}
                          type="radio"
                          disabled={isDisabled}
                          className="mr-1"
                          value="no"
                          {...FACStepOneRegister("foreign")}
                        />
                        {t("AgentFACStepOne-stepOneRegisterOption2")}
                      </label>
                    </div>
                  </fieldset>
                </div>
                <div>
                  {foreignDeath === "yes" && (
                    <>
                      <label
                        htmlFor={`${uuid}-foreignCountry`}
                        className="block mb-1"
                      >
                        {t("AgentFACStepOne-stepOneRegisterQuestion2")}
                      </label>
                      <input
                        disabled={isDisabled}
                        id={`${uuid}-foreignCountry`}
                        {...FACStepOneRegister("foreignCountry")}
                      />
                    </>
                  )}
                </div>
              </div>

              <div className="grid gap-4 grid-cols-1 md:gap-y-0 md:gap-x-4 md:grid-cols-[2fr,2fr]">
                <div className="">
                  <fieldset>
                    <legend className="font-bold">
                      {t("AgentFACStepOne-stepOneRegister-meritalStatus")}
                    </legend>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-marital1`}
                          type="radio"
                          name="marital"
                          className="mr-1"
                          disabled={isDisabled}
                          value="married"
                          {...FACStepOneRegister("marital")}
                        />{" "}
                        {t("AgentFACStepOne-stepOneRegister-marriedLabel")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-marital2`}
                          type="radio"
                          name="marital"
                          disabled={isDisabled}
                          className="mr-1"
                          value="widowed"
                          {...FACStepOneRegister("marital")}
                        />{" "}
                        {t("AgentFACStepOne-stepOneRegister-windowedLabel")}
                      </label>
                    </div>
                    <div className="inline-block mt-2 mr-6">
                      <label className="inline-flex items-center text-xs font-semibold">
                        <input
                          id={`${uuid}-marital3`}
                          type="radio"
                          name="marital"
                          disabled={isDisabled}
                          className="mr-1"
                          value="single"
                          {...FACStepOneRegister("marital")}
                        />{" "}
                        {t("AgentFACStepOne-singleLabel")}
                      </label>
                    </div>
                  </fieldset>
                </div>
                {maritalStatus === "married" && (
                  <div>
                    <label
                      htmlFor={`${uuid}-spouse-name`}
                      className="block mb-1"
                    >
                      {t("AgentFACStepOne-stepOneRegister-spouseNameLabel")}
                    </label>
                    <input
                      disabled={isDisabled}
                      id={`${uuid}-spouse-name`}
                      {...FACStepOneRegister("spouseName")}
                    />
                  </div>
                )}
                {maritalStatus === "widowed" && (
                  <div>
                    <label
                      htmlFor={`${uuid}-widowed-name`}
                      className="block mb-1"
                    >
                      {t("AgentFACStepOne-stepOneRegister-deceasedNameLabel")}
                    </label>
                    <input
                      disabled={isDisabled}
                      id={`${uuid}-widowed-name`}
                      {...FACStepOneRegister("deceasedSpouse")}
                    />
                  </div>
                )}
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <button
                className="ui-btn-primary text-center"
                disabled={isDisabled}
              >
                {t("AgentFACStepOne-stepOneRegister-filingClaimButton")}
              </button>
            </div>
          </form>
        </EverlakeLoadingOverlay>
      )}
    </>
  );
}
